import { GpsSession, TaxiLog } from '@/types/taxi';

type SyncPayload = {
	logs: TaxiLog[];
	gpsSessions: GpsSession[];
	syncedAt: string;
};

type ClaudeSuggestionInput = {
	apiKey: string;
	logs: TaxiLog[];
	gpsSessions: GpsSession[];
	ruleBasedSuggestion: string;
	defaultAreaHint: string;
};

const summarizeLogs = (logs: TaxiLog[]) =>
	logs
		.slice(0, 20)
		.map(
			(log) =>
				`${log.timestamp} ${log.event} revenue=${log.revenue ?? 'null'} memo=${log.memo}`,
		)
		.join('\n');

const summarizeGpsSessions = (sessions: GpsSession[]) =>
	sessions
		.slice(0, 20)
		.map(
			(session) =>
				`${session.startTimestamp} -> ${session.endTimestamp} (${session.durationMinutes}min / ${session.distanceKm}km)`,
		)
		.join('\n');

export const syncToSpreadsheet = async (
	webhookUrl: string,
	payload: SyncPayload,
) => {
	const response = await fetch(webhookUrl, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(payload),
	});

	if (!response.ok) {
		const text = await response.text();
		throw new Error(`スプレッドシート同期に失敗: ${response.status} ${text}`);
	}

	return response.text();
};

export const fetchClaudeSuggestion = async ({
	apiKey,
	logs,
	gpsSessions,
	ruleBasedSuggestion,
	defaultAreaHint,
}: ClaudeSuggestionInput) => {
	if (!apiKey) {
		return ruleBasedSuggestion;
	}

	const prompt = `
あなたは新宿エリア専属のタクシー運行アドバイザーです。
目的は「次の30分で売上期待値を最大化する待機場所」を提案することです。

制約:
- 実行可能で短い指示
- 具体的な場所名と待機時間を必ず含める
- 提案理由を1-2文で記載
- 出力は日本語で120文字以内

補足:
- デフォルト待機候補: ${defaultAreaHint}
- ルールベース提案: ${ruleBasedSuggestion}

直近ログ:
${summarizeLogs(logs)}

直近GPSセッション:
${summarizeGpsSessions(gpsSessions)}
`.trim();

	const response = await fetch('https://api.anthropic.com/v1/messages', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'x-api-key': apiKey,
			'anthropic-version': '2023-06-01',
		},
		body: JSON.stringify({
			model: 'claude-3-5-sonnet-latest',
			max_tokens: 200,
			temperature: 0.3,
			messages: [
				{
					role: 'user',
					content: prompt,
				},
			],
		}),
	});

	if (!response.ok) {
		const text = await response.text();
		throw new Error(`Claude API エラー: ${response.status} ${text}`);
	}

	const json = await response.json();
	const text = json?.content?.[0]?.text;
	if (!text || typeof text !== 'string') {
		throw new Error('Claude API のレスポンス形式が不正です');
	}
	return text.trim();
};
